import { MOBILE_MAX_SIZE, useWindowSize } from '@sendible/design-system';
import { createContext, Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MediaIdType, MediaType } from '@sendible/shared-state-bridge';
import { mediasPerPageDesktop, mediasPerPageMobile, defaultMediaLibrary, mediaLibraryIdParam, mediaSearchParam } from './constants';

export interface MediaToDelete {
  media: MediaType;
  source: 'preview' | 'content';
}

type MediaLibraryContextType = {
  activeMediaLibrary: MediaLibraryType;
  isCreationModalOpen: boolean;
  isDeleteModalOpen: boolean;
  isMobile: boolean;
  isRenameModalOpen: boolean;
  mediaSearch: string;
  mediaToDelete: MediaToDelete | undefined;
  onNavigate: (mediaLibrary: MediaLibraryType) => void;
  perPage: number;
  selectedMediasIds: MediaIdType[];
  setActiveMediaLibrary: Dispatch<SetStateAction<MediaLibraryType>>;
  setIsCreationModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsDeleteModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsRenameModalOpen: Dispatch<SetStateAction<boolean>>;
  setMediaSearch: Dispatch<SetStateAction<string>>;
  setMediaToDelete: Dispatch<SetStateAction<MediaToDelete | undefined>>;
  setSelectedMediasIds: Dispatch<SetStateAction<MediaIdType[]>>;
  setSidebarIsVisible: Dispatch<SetStateAction<boolean>>;
  sidebarIsVisible: boolean;
};

export const MediaLibraryContext = createContext({} as MediaLibraryContextType);

export const MediaLibraryProvider = ({ children }: Component) => {
  const { width } = useWindowSize();
  const [searchParams, setSearchParams] = useSearchParams();

  const [mediaToDelete, setMediaToDelete] = useState<MediaToDelete | undefined>(undefined);
  const [activeMediaLibrary, setActiveMediaLibrary] = useState(defaultMediaLibrary);
  const [sidebarIsVisible, setSidebarIsVisible] = useState(false);
  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [selectedMediasIds, setSelectedMediasIds] = useState<MediaIdType[]>([]);

  // Fix 1: Read structural URL values straight into initial state functions
  const [mediaSearch, setMediaSearch] = useState(() => searchParams.get(mediaSearchParam) || '');

  // Fix 2: Pure derived variables computed directly during the render pass
  const isMobile = width <= MOBILE_MAX_SIZE;
  const perPage = isMobile ? mediasPerPageMobile : mediasPerPageDesktop;

  // Track previous library id and clear selected media when switching libraries.
  const prevLibraryIdRef = useRef<string | null>(defaultMediaLibrary?.id || null);

  const onNavigate = (mediaLibrary: MediaLibraryType) => {
    setMediaSearch('');
    setSearchParams({ ...Object.fromEntries(searchParams), [mediaSearchParam]: '' });
    setActiveMediaLibrary(mediaLibrary);
    setSidebarIsVisible(false);
  };

  // Keep the query parameters updated when users search
  useEffect(() => {
    setSearchParams({ ...Object.fromEntries(searchParams), [mediaSearchParam]: mediaSearch });
  }, [mediaSearch]);

  useEffect(() => {
    const currentLibraryId = activeMediaLibrary?.id || null;

    if (currentLibraryId !== prevLibraryIdRef.current) {
      prevLibraryIdRef.current = currentLibraryId;
      setSelectedMediasIds([]);
    }
  }, [activeMediaLibrary?.id]);

  // Keep the query parameters updated when the library selection moves
  useEffect(() => {
    if (activeMediaLibrary?.id) {
      setSearchParams({ ...Object.fromEntries(searchParams), [mediaLibraryIdParam]: activeMediaLibrary.id });
    }
  }, [activeMediaLibrary.id]);

  return (
    <MediaLibraryContext.Provider
      value={{
        activeMediaLibrary,
        isCreationModalOpen,
        isDeleteModalOpen,
        isMobile,
        isRenameModalOpen,
        mediaSearch,
        mediaToDelete,
        onNavigate,
        perPage,
        selectedMediasIds,
        setActiveMediaLibrary,
        setIsCreationModalOpen,
        setIsDeleteModalOpen,
        setIsRenameModalOpen,
        setMediaSearch,
        setMediaToDelete,
        setSelectedMediasIds,
        setSidebarIsVisible,
        sidebarIsVisible,
      }}
    >
      {children}
    </MediaLibraryContext.Provider>
  );
};

export const useMediaLibraryContext = (): MediaLibraryContextType => useContext(MediaLibraryContext);
